import { Anchor } from "@mantine/core"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { BoolCell, Missing, TextCell, Tooltip } from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import type { FindEcoModulationProductsResponseDto } from "~/gen/api"

const columnHelper = createColumnHelper<FindEcoModulationProductsResponseDto>()

const EcoModulationDatapointCell = ({
  row,
  code,
}: {
  row: FindEcoModulationProductsResponseDto
  code: FindEcoModulationProductsResponseDto["datapoints"][number]["code"]
}) => {
  if (!row.hasEcoModulationData) {
    return <Missing />
  }

  const datapoint = row.datapoints.find((entry) => entry.code === code)

  if (datapoint === undefined) {
    return <Missing />
  }

  if (datapoint.sourcePhrase !== null) {
    return (
      <Tooltip label={datapoint.sourcePhrase}>
        <span>
          <BoolCell value={datapoint.fired} />
        </span>
      </Tooltip>
    )
  }

  return <BoolCell value={datapoint.fired} />
}

export const useEcoModulationColumns = (): ColumnDef<
  FindEcoModulationProductsResponseDto,
  any
>[] => {
  const { t } = useTranslation("regulations")

  return useMemo<ColumnDef<FindEcoModulationProductsResponseDto, any>[]>(() => {
    return [
      columnHelper.accessor("name", {
        id: "name",
        header: t("ecoModulations.columns.name"),
        enableSorting: false,
        enablePinning: true,
        cell: (info) => (
          <Anchor component="span" fz="xs" c="gray.9" td="underline">
            <TextCell value={info.getValue()} />
          </Anchor>
        ),
      }),
      columnHelper.accessor("reference", {
        id: "reference",
        header: t("ecoModulations.columns.reference"),
        enableSorting: false,
        enablePinning: true,
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("purchaseOrderReference", {
        id: "purchaseOrder",
        header: t("ecoModulations.columns.purchaseOrder"),
        enableSorting: false,
        enablePinning: true,
        cell: (info) => <TextCell value={info.getValue() ?? undefined} />,
      }),
      columnHelper.accessor("color", {
        id: "color",
        header: t("ecoModulations.columns.color"),
        enableSorting: false,
        cell: (info) => <TextCell value={info.getValue() ?? undefined} />,
      }),
      columnHelper.accessor("collection", {
        id: "collection",
        header: t("ecoModulations.columns.collection"),
        enableSorting: false,
        cell: (info) => <TextCell value={info.getValue() ?? undefined} />,
      }),
      columnHelper.display({
        id: "bonusDurability",
        header: t("ecoModulations.columns.bonusDurability"),
        enableSorting: false,
        cell: ({ row }) => (
          <EcoModulationDatapointCell
            row={row.original}
            code="BONUS_DURABILITY"
          />
        ),
      }),
      columnHelper.display({
        id: "bonusEnvironmentalLabels",
        header: t("ecoModulations.columns.bonusEnvironmentalLabels"),
        enableSorting: false,
        cell: ({ row }) => (
          <EcoModulationDatapointCell
            row={row.original}
            code="BONUS_ENVIRONMENTAL_LABELS"
          />
        ),
      }),
      columnHelper.display({
        id: "bonusRecycledMaterials",
        header: t("ecoModulations.columns.bonusRecycledMaterials"),
        enableSorting: false,
        cell: ({ row }) => (
          <EcoModulationDatapointCell
            row={row.original}
            code="BONUS_RECYCLED_MATERIALS"
          />
        ),
      }),
      columnHelper.display({
        id: "malusMetalloplasticFibers",
        header: t("ecoModulations.columns.malusMetalloplasticFibers"),
        enableSorting: false,
        cell: ({ row }) => (
          <EcoModulationDatapointCell
            row={row.original}
            code="MALUS_METALLOPLASTIC_FIBERS"
          />
        ),
      }),
      columnHelper.display({
        id: "malusEeeComponents",
        header: t("ecoModulations.columns.malusEeeComponents"),
        enableSorting: false,
        cell: ({ row }) => (
          <EcoModulationDatapointCell
            row={row.original}
            code="MALUS_EEE_COMPONENTS"
          />
        ),
      }),
    ]
  }, [t])
}
